import { Matrix4, Vector3 } from 'three';

import { FatLine } from '@/lib/three';
import { ILineOptions, IPolyOptions, IPolyUserData, WidgetObject } from '@/types';

import { Line3d, LineUpdateOptions } from './Line3d';

const _vec = new Vector3();

export class Poly3d {
  static MIN_POINTS_COUNT = 3;

  static create(data: WidgetObject['data'], options: IPolyOptions): FatLine {
    const { isClose = false, ...otherOptions } = options;
    const line = Line3d.create(data, otherOptions);
    line.userData = { type: 'poly3d', ...options } satisfies IPolyUserData;

    // 如果是闭合的，就需要把第一个点加到最后
    if (isClose) {
      Poly3d.closeLine(line);
    }

    return line;
  }

  /**
   * 全量更新
   * @param target
   * @param data
   */
  static update(target: FatLine, data: WidgetObject['data']) {
    Poly3d.partialUpdate(target, data, { startIndex: 0, mode: 'replace' });
  }

  /**
   * 局部更新
   * @param target
   * @param data
   * @param options
   * @returns
   */
  static partialUpdate(target: FatLine, data: WidgetObject['data'], options?: LineUpdateOptions) {
    if (!target.userData.isClose) {
      Line3d.partialUpdate(target, data, options);
      return;
    }

    const { startIndex = 0, mode = 'update' } = options || {};
    const start = startIndex * 3;

    // 此处得到的 position，首尾的数值是一样的
    let positions = Line3d.getVertexFromObject3D(target);

    let updateData: Array<number> | Float32Array = [];
    let end = positions.length;

    switch (mode) {
      case 'update':
        updateData = data;
        end = start + data.length;
        // 同时也是更新最后的位置
        if (start === 0) {
          target.geometry.updatePositions(updateData.slice(0, 3), positions.length - 3);
        }
        break;

      case 'replace':
        updateData = data.concat(start === 0 ? data.slice(0, 3) : [...positions.slice(0, 3)]);
        end = start + updateData.length;
        break;

      case 'add':
        updateData = [...data, ...positions.slice(start, end)];
        end += data.length;
        // 同时也是更新最后的位置
        if (start === 0) {
          const [x, y, z] = updateData,
            lastPos = updateData.length - 3;
          updateData[lastPos] = x;
          updateData[lastPos + 1] = y;
          updateData[lastPos + 2] = z;
        }
        break;

      case 'remove':
        // 仅删除一个点
        updateData = positions.slice(start + 3, end);
        end -= 3;
        // 同时也是更新最后的位置
        if (start === 0) {
          const [x, y, z] = updateData,
            lastPos = updateData.length - 3;
          updateData[lastPos] = x;
          updateData[lastPos + 1] = y;
          updateData[lastPos + 2] = z;
        }
        break;

      default:
        break;
    }

    const pointCount = Math.floor(
      (mode === 'remove' || mode === 'replace' ? end : Math.max(end, positions.length)) / 3
    );

    Line3d.partialUpdateFromVertexs(target, positions, { start, end, updateData, pointCount });
  }

  // 结束编辑的时候，需要把最后的点加上
  static closeLine(target: FatLine) {
    target.userData.isClose = true;
    let positions = Line3d.getVertexFromObject3D(target);
    target.geometry.pointCount = positions.length / 3 + 1;

    if (positions.length / 3 > target.geometry.attributes.instanceEnd.count) {
      const newPositions = [...positions, ...positions.slice(0, 3)];
      target.geometry.instanceCount = Math.max(newPositions.length / 3 - 1, 0);
      target.geometry.setPositions(newPositions);
      return;
    }

    target.geometry.instanceCount = Math.max(positions.length / 3, 0);
    target.geometry.updatePositions(positions.slice(0, 3), positions.length);
  }

  static createFromWidget(name: string, widget: WidgetObject, matrix: Matrix4, options: ILineOptions): FatLine {
    const line = Poly3d.create(Line3d.pointsApplyMatrix4(widget.data, matrix), { ...options, isClose: true });
    line.name = name;
    return line;
  }

  static transOutWidget(target: FatLine, matrix: Matrix4): WidgetObject {
    const data = Line3d.pointsApplyMatrix4(Poly3d.getVertexFromObject3D(target), matrix);

    return {
      name: 'poly3d',
      data,
      line_type: 'line',
    };
  }

  static updateFromWidget(target: FatLine, widget: WidgetObject, matrix: Matrix4) {
    Poly3d.update(target, Line3d.pointsApplyMatrix4(widget.data, matrix));
  }

  static getVertexCount(target: FatLine) {
    if (target.userData.isClose) return target.geometry.pointCount - 1;
    return target.geometry.pointCount;
  }

  static getVertexFromObject3D(target: FatLine) {
    if (target.userData.isClose) {
      if (target.geometry.pointCount === 0) return [];

      const start = target.geometry.attributes.instanceStart;
      const segmentCount = Math.min(target.geometry.instanceCount, start.count);

      if (segmentCount === 0) {
        return _vec.fromBufferAttribute(start, 0).toArray();
      }
      // 在多边形中，线段数和点数是一样的
      const positions = new Float32Array(segmentCount * 3);
      for (let i = 0, j = 0; i < segmentCount; i++, j += 3) {
        _vec.fromBufferAttribute(start, i).toArray(positions, j);
      }
      return positions;
    }

    return Line3d.getVertexFromObject3D(target);
  }
}
